import { useEffect, useRef, useState } from "react";
import { UtensilsCrossed, Save, Info, Tag, Plus, X, Bluetooth, Printer } from "lucide-react";
import api, { formatErr } from "@/lib/api";
import { useDialog } from "@/context/DialogProvider";
import { renderFoodLabel } from "@/lib/foodLabel";

const DPI_OPTIONS = [203, 300, 600];

export default function AdminFoodPrep() {
  const { notify } = useDialog();
  const [cfg, setCfg] = useState(null);
  const [width, setWidth] = useState(50);
  const [height, setHeight] = useState(40);
  const [dpi, setDpi] = useState(203);
  const [printerEnabled, setPrinterEnabled] = useState(false);
  const [command, setCommand] = useState("tspl");
  const [gap, setGap] = useState(2);
  const [svcUuid, setSvcUuid] = useState("");
  const [writeUuid, setWriteUuid] = useState("");
  const [saving, setSaving] = useState(false);
  const previewRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/food-prep-config");
      setCfg(data); setWidth(data.label_width_mm); setHeight(data.label_height_mm); setDpi(data.label_dpi);
      setPrinterEnabled(!!data.printer_enabled);
      setCommand(data.printer_command || "tspl");
      setGap(data.printer_gap_mm ?? 2);
      setSvcUuid(data.ble_service_uuid || "");
      setWriteUuid(data.ble_write_uuid || "");
    } catch (e) { console.debug(e); }
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!previewRef.current) return;
    const w = Number(width) || 50, h = Number(height) || 40;
    renderFoodLabel(previewRef.current, {
      name: "Chicken Stock",
      useByText: "28 Jun, 02:00 PM",
      prepText: "Prep: 26 Jun, 02:00 PM",
      byText: "By: J. Ong",
      token: "VBE1:preview",
    }, { widthMm: w, heightMm: h, dpi: 203 }).catch(() => {});
  }, [width, height, dpi]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/food-prep-config", {
        label_width_mm: Number(width), label_height_mm: Number(height), label_dpi: Number(dpi),
        printer_enabled: printerEnabled,
        printer_command: command,
        printer_gap_mm: Number(gap),
        ble_service_uuid: svcUuid,
        ble_write_uuid: writeUuid,
      });
      setCfg(data);
      await notify({ title: "Saved", message: "Label size applied for all customers.", variant: "ok" });
    } catch (e) {
      await notify({ title: "Couldn't save", message: formatErr(e.response?.data?.detail), variant: "err" });
    } finally { setSaving(false); }
  };

  return (
    <div className="vb-fade-in" data-testid="admin-food-prep-page" style={{ maxWidth: 900 }}>
      <header className="mb-4">
        <h1 className="vb-heading text-3xl m-0 mb-1" style={{ color: "var(--vb-text-2)" }}>
          <UtensilsCrossed size={24} className="inline-block mr-2 -mt-1" /> Food Prep — Print Settings
        </h1>
        <p className="text-sm opacity-70 m-0">
          Platform-wide thermal label size. Applies to <strong>all customers</strong> for now; selectable sizes can be added later.
        </p>
      </header>

      <div className="vb-card p-4 mb-4 flex items-start gap-2" data-testid="admin-food-prep-note">
        <Info size={16} className="mt-0.5 shrink-0" style={{ color: "var(--vb-text-2)" }} />
        <span className="text-sm opacity-80">No fixed label size has been finalised yet. Set the dimensions here once your thermal labels are chosen — every tenant's printed food labels will use these values immediately.</span>
      </div>

      <div className="grid gap-4 grid-cols-1 md:grid-cols-[minmax(0,320px)_1fr]">
        <div className="vb-card p-5 h-fit">
          <h3 className="vb-heading text-lg mt-0 mb-3">Label dimensions</h3>
          <label className="block mb-3 text-sm">
            <span className="block mb-1 font-medium">Width (mm)</span>
            <input className="vb-input" type="number" min="20" max="150" value={width}
              onChange={(e) => setWidth(e.target.value)} data-testid="admin-label-width" />
          </label>
          <label className="block mb-3 text-sm">
            <span className="block mb-1 font-medium">Height (mm)</span>
            <input className="vb-input" type="number" min="15" max="150" value={height}
              onChange={(e) => setHeight(e.target.value)} data-testid="admin-label-height" />
          </label>
          <label className="block mb-4 text-sm">
            <span className="block mb-1 font-medium">Print resolution (DPI)</span>
            <select className="vb-input" value={dpi} onChange={(e) => setDpi(e.target.value)} data-testid="admin-label-dpi">
              {DPI_OPTIONS.map((d) => <option key={d} value={d}>{d} dpi</option>)}
            </select>
          </label>
          <button className="vb-btn w-full" disabled={saving} onClick={save} data-testid="admin-food-prep-save">
            <Save size={16} /> {saving ? "Saving…" : "Save for all customers"}
          </button>
          {cfg?.updated_at && (
            <p className="text-xs opacity-50 mt-2">Last updated by {cfg.updated_by_email || "—"}.</p>
          )}
        </div>

        <div className="vb-card p-5">
          <h3 className="vb-heading text-lg mt-0 mb-3">Live preview</h3>
          <div className="flex justify-center" style={{ background: "var(--vb-bg)", padding: 16, borderRadius: 8, overflow: "hidden" }}>
            <canvas ref={previewRef} data-testid="admin-label-preview"
              style={{ width: 340, maxWidth: "100%", height: "auto", aspectRatio: `${width} / ${height}`, border: "1px solid var(--vb-border)", borderRadius: 6, background: "#fff" }} />
          </div>
          <p className="text-xs opacity-60 mt-3 text-center">{width} × {height} mm — sample label</p>
        </div>
      </div>

      <div className="mt-4">
        <div className="vb-card p-5" data-testid="admin-printer-settings">
          <h3 className="vb-heading text-lg mt-0 mb-1 flex items-center gap-2">
            <Bluetooth size={18} /> Direct Bluetooth printing
          </h3>
          <p className="text-sm opacity-70 mb-4">
            When enabled, staff tap <strong>Print</strong> and the label goes straight to the paired Bluetooth
            label printer — no print preview. Applies to <strong>all customers</strong>. Works on Chrome for
            Android (the standard preview button stays available as a fallback on iPhone/desktop).
          </p>

          <label className="flex items-center gap-3 mb-4 cursor-pointer select-none">
            <input type="checkbox" checked={printerEnabled}
              onChange={(e) => setPrinterEnabled(e.target.checked)}
              data-testid="admin-printer-enabled" style={{ width: 18, height: 18 }} />
            <span className="text-sm font-medium">Enable direct Bluetooth printing for all customers</span>
          </label>

          <div className="grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))" }}>
            <label className="block text-sm">
              <span className="block mb-1 font-medium">Printer language</span>
              <select className="vb-input" value={command} onChange={(e) => setCommand(e.target.value)} data-testid="admin-printer-command">
                <option value="tspl">TSPL (JiaBo / Gprinter label printers)</option>
                <option value="escpos">ESC/POS (receipt printers)</option>
              </select>
            </label>
            <label className="block text-sm">
              <span className="block mb-1 font-medium">Label gap (mm)</span>
              <input className="vb-input" type="number" min="0" max="10" step="0.5" value={gap}
                onChange={(e) => setGap(e.target.value)} data-testid="admin-printer-gap" />
            </label>
          </div>

          <details className="mt-4">
            <summary className="text-sm font-medium cursor-pointer opacity-80">Advanced — BLE UUID overrides (optional)</summary>
            <p className="text-xs opacity-60 mt-2 mb-3">
              Leave blank to auto-discover the printer's write channel (recommended). Only fill these in if
              auto-discovery fails — copy the Service &amp; Write characteristic UUIDs from the nRF Connect app.
            </p>
            <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
              <label className="block text-sm">
                <span className="block mb-1 font-medium">Service UUID</span>
                <input className="vb-input" value={svcUuid} onChange={(e) => setSvcUuid(e.target.value)}
                  placeholder="0000ff00-0000-1000-8000-00805f9b34fb" data-testid="admin-printer-service-uuid" />
              </label>
              <label className="block text-sm">
                <span className="block mb-1 font-medium">Write characteristic UUID</span>
                <input className="vb-input" value={writeUuid} onChange={(e) => setWriteUuid(e.target.value)}
                  placeholder="0000ff02-0000-1000-8000-00805f9b34fb" data-testid="admin-printer-write-uuid" />
              </label>
            </div>
          </details>

          <div className="vb-card p-3 mt-4 flex items-start gap-2" style={{ background: "var(--vb-bg)" }}>
            <Printer size={15} className="mt-0.5 shrink-0" style={{ color: "var(--vb-text-2)" }} />
            <span className="text-xs opacity-75">
              First print on each device shows a one-time Bluetooth pairing chooser — pick your printer once, and
              it reconnects automatically after that. Make sure the label size above matches the physical media loaded.
            </span>
          </div>

          <button className="vb-btn mt-4" disabled={saving} onClick={save} data-testid="admin-printer-save">
            <Save size={16} /> {saving ? "Saving…" : "Save printer settings"}
          </button>
        </div>
      </div>

      <div className="mt-4">
        <DefaultCategoriesCard />
      </div>
    </div>
  );
}

function DefaultCategoriesCard() {
  const { notify, confirm } = useDialog();
  const [cats, setCats] = useState([]);
  const [name, setName] = useState("");
  const [adding, setAdding] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/admin/food-prep-categories"); setCats(data.categories || []); }
    catch (e) { console.debug(e); }
  };
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e?.preventDefault();
    if (!name.trim()) return;
    setAdding(true);
    try { await api.post("/admin/food-prep-categories", { name: name.trim() }); setName(""); load(); }
    catch (e) { await notify({ title: "Couldn't add", message: formatErr(e.response?.data?.detail), variant: "err" }); }
    finally { setAdding(false); }
  };
  const remove = async (c) => {
    if (!(await confirm({ title: "Delete default category?", message: `Remove “${c.name}” from the platform defaults? New customers won't see it; customers who already use it keep their presets.`, danger: true, confirmLabel: "Delete" }))) return;
    try { await api.delete(`/admin/food-prep-categories/${c.id}`); load(); }
    catch (e) { await notify({ title: "Failed", message: formatErr(e.response?.data?.detail), variant: "err" }); }
  };

  return (
    <div className="vb-card p-5" data-testid="admin-food-categories">
      <h3 className="vb-heading text-lg mt-0 mb-1 flex items-center gap-2"><Tag size={16} /> Default Food Categories</h3>
      <p className="text-sm opacity-70 mb-3">
        These categories appear for <strong>every customer</strong> in their Food Catalog. Customers can still add their own categories within their own workspace.
      </p>
      <form className="flex gap-2 mb-3" style={{ maxWidth: 420 }} onSubmit={add}>
        <input className="vb-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Sauces" data-testid="admin-category-name" />
        <button className="vb-btn sm shrink-0" disabled={adding} data-testid="admin-category-add"><Plus size={14} /> Add</button>
      </form>
      {cats.length === 0 ? (
        <div className="text-sm opacity-60">No default categories yet. Add a few common ones (Sauces, Soups, Proteins, Dairy…).</div>
      ) : (
        <div className="flex flex-wrap gap-2" data-testid="admin-category-list">
          {cats.map((c) => (
            <span key={c.id} className="vb-pill" style={{ paddingRight: 6 }} data-testid={`admin-category-pill-${c.id}`}>
              {c.name}
              <button type="button" className="ml-1 opacity-60 hover:opacity-100" onClick={() => remove(c)} data-testid={`admin-category-delete-${c.id}`} aria-label={`Delete ${c.name}`}>
                <X size={12} />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
